// 初回ヒアリング画面 (要件 §7-2)
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth";
import type { HearingQuestionDTO } from "../../shared/dto";
import { ErrorBanner, SuccessBanner, Banner } from "../components/ui";
import { BrandLockup } from "../components/BrandLockup";

interface AnswersMap {
  [code: string]: { type: HearingQuestionDTO["type"]; valueText?: string; valueJson?: unknown };
}

export function FirstHearingPage() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [questions, setQuestions] = useState<HearingQuestionDTO[]>([]);
  const [answers, setAnswers] = useState<AnswersMap>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      api.get<{ questions: HearingQuestionDTO[] }>("/member/hearing/questions"),
      api.get<{
        items: { code: string; type: HearingQuestionDTO["type"]; valueText: string | null; valueJson: string | null }[];
      }>("/member/hearing/me"),
    ]).then(([q, me]) => {
      setQuestions(q.questions);
      const map: AnswersMap = {};
      for (const i of me.items ?? []) {
        map[i.code] = {
          type: i.type,
          valueText: i.valueText ?? undefined,
          valueJson: i.valueJson ? JSON.parse(i.valueJson) : undefined,
        };
      }
      setAnswers(map);
    });
  }, []);

  const setVal = (q: HearingQuestionDTO, val: { valueText?: string; valueJson?: unknown }) => {
    setAnswers((prev) => ({ ...prev, [q.code]: { type: q.type, ...val } }));
  };

  const items = () =>
    Object.entries(answers).map(([code, v]) => ({
      code,
      type: v.type,
      valueText: v.valueText ?? null,
      valueJson: v.valueJson,
    }));

  const save = async () => {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      await api.post("/member/hearing/me/save", { items: items() });
      setMsg("下書きを保存しました");
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const complete = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.post("/member/hearing/me/save", { items: items() });
      await api.post("/member/hearing/me/complete");
      await refresh();
      nav("/", { replace: true });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // 進捗計算: 入力済み (valueText が空でない、または valueJson が非空配列) の項目数
  const filledCount = questions.reduce((n, q) => {
    const v = answers[q.code];
    const hasText = !!(v?.valueText && v.valueText.length > 0);
    const hasJson =
      Array.isArray(v?.valueJson) && (v?.valueJson as unknown[]).length > 0;
    return n + (hasText || hasJson ? 1 : 0);
  }, 0);
  const requiredMissing = questions
    .filter((q) => q.required)
    .filter((q) => {
      const v = answers[q.code];
      return !(v?.valueText && v.valueText.length > 0);
    });
  const progressPct = questions.length
    ? Math.round((filledCount / questions.length) * 100)
    : 0;

  return (
    <div className="min-h-screen flex flex-col bg-tora-paper">
      <header className="bg-white border-b-2 border-tora-accent">
        <div className="mx-auto max-w-6xl px-4 py-4">
          <BrandLockup />
        </div>
      </header>

      <main className="flex-1 py-8 px-4">
        <div className="max-w-2xl mx-auto card p-6 md:p-8 shadow-md">
          <p className="text-[11px] text-tora tracking-widest font-semibold">
            FIRST HEARING
          </p>
          <h1 className="text-2xl md:text-3xl font-bold text-tora-ink mt-1.5">
            初回ヒアリング
          </h1>
          <p className="text-sm text-tora-ink/70 mt-2 leading-relaxed">
            会員サポートとマッチングのために、最初に少しお伺いします。
            途中保存もできます (下書き保存)。
          </p>
          <p className="text-xs text-tora-ink/60 mt-2">
            <span className="text-tora font-bold">*</span>
            が付いている項目は必須です (氏名・表示名)。それ以外は任意で、
            後から変更できます。
          </p>

          {/* 進捗バー */}
          <div className="mt-5">
            <div className="flex items-center justify-between text-xs text-tora-ink/60 mb-1">
              <span>
                入力済み {filledCount} / {questions.length} 項目
              </span>
              <span className="font-semibold text-tora">{progressPct}%</span>
            </div>
            <div className="h-2.5 bg-black/5 rounded-full overflow-hidden">
              <div
                className="h-full bg-tora transition-all"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>

          <div className="mt-6 space-y-5">
            {questions.map((q) => {
              const missing = requiredMissing.some((m) => m.code === q.code);
              return (
                <Field
                  key={q.code}
                  q={q}
                  value={answers[q.code]}
                  onChange={(v) => setVal(q, v)}
                  highlightMissing={err != null && missing}
                />
              );
            })}
          </div>

          {err && (
            <div className="mt-4">
              <ErrorBanner>{err}</ErrorBanner>
            </div>
          )}
          {msg && (
            <div className="mt-4">
              <SuccessBanner>{msg}</SuccessBanner>
            </div>
          )}

          {/* 必須未入力の Warning (CTA 直上) */}
          {!busy && requiredMissing.length > 0 && (
            <div className="mt-4">
              <Banner tone="warning">
                必須項目が {requiredMissing.length} 件未入力です。
                「送信して完了」には全必須項目の入力が必要です。
              </Banner>
            </div>
          )}

          <div className="mt-6 flex flex-col md:flex-row flex-wrap gap-3 md:gap-2 md:items-center">
            <button
              onClick={complete}
              className="btn-primary btn-lg w-full md:w-auto md:px-6 order-1 md:order-2"
              disabled={busy}
            >
              {busy ? "送信中…" : "送信して完了 →"}
            </button>
            <button
              onClick={save}
              className="btn-outline w-full md:w-auto order-2 md:order-1"
              disabled={busy}
            >
              下書き保存
            </button>
            <p className="text-xs text-tora-ink/60 md:ml-auto order-3">
              {busy
                ? "送信中…"
                : requiredMissing.length > 0
                  ? `必須未入力 ${requiredMissing.length} 件`
                  : "✓ 必須入力済み"}
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}

function Field({
  q,
  value,
  onChange,
  highlightMissing,
}: {
  q: HearingQuestionDTO;
  value?: { valueText?: string; valueJson?: unknown };
  onChange: (v: { valueText?: string; valueJson?: unknown }) => void;
  highlightMissing?: boolean;
}) {
  const id = `q-${q.code}`;
  const inputExtra = highlightMissing
    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
    : "";
  const label = (
    <label htmlFor={id} className="label">
      {q.label}
      {q.required && (
        <span className="ml-1 text-red-600" title="必須">
          *
        </span>
      )}
      {highlightMissing && (
        <span className="ml-2 text-xs text-red-600">未入力</span>
      )}
    </label>
  );
  switch (q.type) {
    case "text":
      return (
        <div>
          {label}
          <input
            id={id}
            className={`input ${inputExtra}`}
            value={value?.valueText ?? ""}
            onChange={(e) => onChange({ valueText: e.target.value })}
          />
        </div>
      );
    case "textarea":
      return (
        <div>
          {label}
          <textarea
            id={id}
            className={`input min-h-[80px] ${inputExtra}`}
            value={value?.valueText ?? ""}
            onChange={(e) => onChange({ valueText: e.target.value })}
          />
        </div>
      );
    case "select":
      return (
        <div>
          {label}
          <select
            id={id}
            className={`input ${inputExtra}`}
            value={value?.valueText ?? ""}
            onChange={(e) => onChange({ valueText: e.target.value })}
          >
            <option value="">選択してください</option>
            {(q.options ?? []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      );
    case "multi_select": {
      const selected: string[] = Array.isArray(value?.valueJson)
        ? (value!.valueJson as string[])
        : [];
      return (
        <div>
          {label}
          <div className="grid grid-cols-2 gap-1">
            {(q.options ?? []).map((o) => {
              const checked = selected.includes(o.value);
              return (
                <label key={o.value} className="text-sm flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? [...selected, o.value]
                        : selected.filter((x) => x !== o.value);
                      onChange({
                        valueJson: next,
                        valueText: next.join(","),
                      });
                    }}
                  />
                  {o.label}
                </label>
              );
            })}
          </div>
        </div>
      );
    }
    case "boolean":
      return (
        <div>
          {label}
          <label className="text-sm flex items-center gap-2">
            <input
              type="checkbox"
              checked={value?.valueText === "true"}
              onChange={(e) =>
                onChange({ valueText: e.target.checked ? "true" : "false" })
              }
            />
            はい
          </label>
        </div>
      );
    default:
      return (
        <div>
          {label}
          <input
            id={id}
            className={`input ${inputExtra}`}
            value={value?.valueText ?? ""}
            onChange={(e) => onChange({ valueText: e.target.value })}
          />
        </div>
      );
  }
}
